import * as Sentry from "@sentry/react";
import { apiRequest } from "./apiRequest";

const ALREADY_REPORTED_BY_API_REQUEST = new Set([
    "ERROR_API_CALL_EXPIRED",
    "ERROR_API_CALL_INVALID_API_ACCESS_TOKEN",
    "ERROR_SQL",
]);

// The single `baseQuery` behind every RTK Query slice in src/features/. Fifteen
// byte-identical copies of this used to live one per slice file.
//
// The reason it is shared rather than duplicated is the `status === 1` check
// below. None of the fifteen copies had it, and adding it in fifteen places is
// how it ends up in fourteen.
//
// -----------------------------------------------------------------------------
// Why `status === 1` matters
// -----------------------------------------------------------------------------
// app_endpoints.php signals *application* errors as HTTP **200** with
// `{status: 1, error_code, error_message}` in the body. Only transport failures
// (non-2xx, timeout) reject the axios promise. So a `try`/`catch` around
// apiRequest — which every copy had — catches the network and none of the
// business logic: a write the backend refused arrived as a fulfilled query and
// rendered a success confirmation.
//
// On the Royal Momentum visit flow that is silent data loss against RCCL
// trade-activation KPIs: a rep completes a visit, sees it saved, and nothing
// persisted. It also masks authorization failures — if the backend starts
// returning `status: 1` for unauthorized requests, this layer would render them
// as success.
//
// -----------------------------------------------------------------------------
// The error shape
// -----------------------------------------------------------------------------
// `status: "APPLICATION_ERROR"` is the discriminator that separates a backend
// rejection from a transport failure, which keeps a numeric HTTP status. The
// original body is preserved under `data` so a caller that needs to explain a
// specific rejection to the user still can — `error_code` and `error_message`
// are lifted to the top level because reading them is the common case.
//
// Callers using `.unwrap()` now reject and land in their existing `catch`, which
// is the point: the confirmation is skipped. Callers awaiting the raw result get
// `{error}` instead of `{data}` — see readFailure() in
// RoyalMomentumAddEditContact.jsx for the one place that reads the code back.
//
// ERROR_INVALID_USER_SESSION never reaches here: apiRequest handles it centrally
// by forcing a logout and returning a promise that never settles.
export const apiRequestBaseQuery = async ({ requestType: reqType, data }) => {
    try {
        const response = await apiRequest({
            payload: { data },
            requestType: reqType,
        });

        if (response?.data?.status === 1) {
            const errorCode = response.data.error_code ?? null;

            // Telemetry, because this class of failure was invisible until now and
            // "which endpoints reject, and how often" is not knowable from the
            // frontend otherwise. Sentry groups by message, so the number of groups
            // is bounded by request types rather than by request volume.
            //
            // The two auth-mechanism codes are skipped because apiRequest already
            // captures them, and ERROR_SQL because it already raises a user-visible
            // popup there. Expect some of what remains to be ordinary user error —
            // a duplicate contact email arrives as ERROR_SALESFORCE_CREATE_FAILED.
            // If that proves noisy, narrow this rather than the check above it.
            if (!ALREADY_REPORTED_BY_API_REQUEST.has(errorCode)) {
                Sentry.captureMessage(
                    `${reqType} rejected by backend${errorCode ? ` (${errorCode})` : ""}`,
                    "warning"
                );
            }

            return {
                error: {
                    status: "APPLICATION_ERROR",
                    error_code: errorCode,
                    error_message: response.data.error_message ?? null,
                    data: response.data,
                },
            };
        }

        return { data: response.data };
    } catch (error) {
        return {
            error: {
                status: error.response?.status || 500,
                data: error.response?.data || error.message,
            },
        };
    }
};
